import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ValidationRegEx } from '@shared/components/RegEx/regEx-list';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import * as IBAN from 'iban';

@Component({
  selector: 'app-base-validation',
  template: '',
})
export class BaseValidationComponent implements OnInit, OnDestroy {
  @Input() sameValue: string = null;
  @Input() validation: object | string = null; // can be object with your validation or prepared string from this component
  @Input() type: string = null;
  @Input() initValue: any = null;
  @Input() correctForm: UntypedFormGroup = null;
  formEl: UntypedFormControl;
  @Output() phInputChange: EventEmitter<any> = new EventEmitter();
  @Input() name: string = 'phInput';
  saveType: string = null;
  someSubscriptions: Subscription = new Subscription();
  formControlChanged: Subject<boolean> = new BehaviorSubject(false);

  constructor() {}

  ngOnInit() {
    if (this.validation) {
      this.initValidation();
    }
    this.saveType = this.type;
    if (this.sameValue) {
      this.subOnSameValue();
    }
  }

  ngOnDestroy() {
    this.someSubscriptions.unsubscribe();
  }

  validationType(event): boolean {
    if (this.type === 'number') {
      const charCode = event.which ? event.which : event.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        return false;
      }
    }
    return true;
  }

  subOnSameValue() {
    // subscribe on change from input with name 'this.sameValue'
    const subValueSame = this.correctForm.get(this.sameValue).valueChanges.subscribe(() => {
      // timeout need, because we must wait input changed
      setTimeout(() => {
        this.formEl.updateValueAndValidity();
      }, 100);
    });
    this.someSubscriptions.add(subValueSame);
  }

  initValidation() {
    let elValidation = null;
    if (typeof this.validation === 'string') {
      switch (this.validation) {
        case 'required':
          elValidation = this.validationRequired;
          break;
        case 'url':
          elValidation = this.validationUrl;
          break;
        case 'urlnotrequired':
          elValidation = this.validationUrlNotRequired;
          break;
        case 'base':
          elValidation = this.validationBase;
          break;
        case 'comment':
          elValidation = this.validationComment;
          break;
        case 'namesurname':
          elValidation = this.validationNameSurname;
          break;
        case 'namesurnamereq':
          elValidation = this.validationNameSurnameReq;
          break;
        case 'nickname':
          elValidation = this.validationNickname;
          break;
        case 'basetext':
          elValidation = this.validationBaseText;
          break;
        case 'basetextrequired':
          elValidation = this.validationBaseTextRequired;
          break;
        case 'longtext':
          elValidation = this.validationLongText;
          break;
        case 'requiredtrim':
          elValidation = this.validationRequiredTrim;
          break;
        case 'textarealongtext':
          elValidation = this.validationTextareaLongText;
          break;
        case 'password':
          elValidation = this.validationPassword;
          break;
        case 'donate':
          elValidation = this.validationDonate;
          break;
        case 'donateMin3':
          elValidation = this.validationDonateMinimum3;
          break;
        case 'participants_project':
          elValidation = this.validationParticipantsProject;
          break;
        case 'email':
          elValidation = this.validationEmail;
          break;
        case 'emailnotrequired':
          elValidation = this.validationEmailNotRequired;
          break;
        case 'contacttopic':
          elValidation = this.validationContactTopic;
          break;
        case 'contactemail':
          elValidation = this.validationContactEmail;
          break;
        case 'contactmessage':
          elValidation = this.validationContactMessage;
          break;
        case 'iban':
          elValidation = this.validationIban;
          break;
        case 'ibanPL':
          elValidation = this.validationIbanPL;
          break;
        case 'country_iso':
          elValidation = this.validationBankAccountCountryIso;
          break;
        case 'branch_code':
          elValidation = this.validationBankAccountBranchCode;
          break;
        case 'bank_name':
          elValidation = this.validationBankAccountBankName;
          break;
        case 'institution_number':
          elValidation = this.validationBankAccountInstitutionNumber;
          break;
        case 'sort_code':
          elValidation = this.validationBankAccountSortCode;
          break;
        case 'aba':
          elValidation = this.validationBankAccountAba;
          break;
        case 'bic':
          elValidation = this.validationBankAccountBic;
          break;
        case 'account_number':
          elValidation = this.validationBankAccountNumber;
          break;
        case 'account_number_gb':
          elValidation = this.validationBankAccountNumberGB;
          break;
        case 'account_number_ca':
          elValidation = this.validationBankAccountNumberCA;
          break;
        case 'account_number_us':
          elValidation = this.validationBankAccountNumberUS;
          break;
        case 'description_event':
          elValidation = this.validationDescriptionEvent;
          break;
        case 'description_ambassador':
          elValidation = this.validationDescriptionAmbassador;
          break;
        case 'checkbox':
          elValidation = this.validationCheckbox;
          break;
        case 'socialMediaLink':
          elValidation = this.validationSocialMediaLink;
          break;
        case 'ambassador_texts':
          elValidation = this.validationAmbassadorTextInputs;
          break;
        case 'ambassador_name_surname':
          elValidation = this.validationAmbassadorNameSurname;
          break;
        case 'phone_number':
          elValidation = [];
          break;
        case 'empty':
          elValidation = [];
          break;
      }

      // if type is empty (or you forgot about him) -> add type from validation (if this string)
      if (!this.type) {
        console.warn('Input not have a type! So type was based on validation');
        this.type = this.validation;
      }
    } else {
      elValidation = this.validation;
    }

    this.formEl = new UntypedFormControl(this.initValue, elValidation);

    const subValueForm = this.formEl.valueChanges.subscribe((value) => {
      this.formControlChanged.next(value ? true : false);
      this.phInputChange.emit(this.formEl);
    });
    this.someSubscriptions.add(subValueForm);

    // add to form, if this.correctForm is not empty ( if empty -> you can get access from emit phInputChange)
    if (this.correctForm) {
      this.correctForm.addControl(this.name, this.formEl);
    }
  }

  get validationPassword() {
    return Validators.compose([
      Validators.required,
      Validators.minLength(8),
      this.regexValidator(ValidationRegEx.oneNumber),
      this.regexValidator(ValidationRegEx.oneCapitalLetter),
      this.checkSamePassword({ error: 'notSamePass' }),
    ]);
  }

  checkSamePassword({ error }): ValidatorFn {
    if (!this.sameValue) {
      return null;
    }
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      return control.value === this.correctForm.value[this.sameValue] ? null : error;
    };
  }

  get validationNameSurname() {
    return Validators.compose([
      Validators.minLength(2),
      Validators.maxLength(255),
      this.regexValidator(ValidationRegEx.nameSurname),
      this.trimText({ error: 'trimText' }),
    ]);
  }

  get validationNameSurnameReq() {
    return Validators.compose([
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(255),
      this.regexValidator(ValidationRegEx.nameSurname),
      this.trimText({ error: 'trimText' }),
    ]);
  }

  get validationNickname() {
    return Validators.compose([
      Validators.minLength(2),
      Validators.maxLength(128),
      this.regexValidator(ValidationRegEx.text),
      this.trimText({ error: 'trimText' }),
    ]);
  }

  get validationDescriptionEvent() {
    return Validators.compose([
      Validators.minLength(20),
      Validators.maxLength(1000),
      Validators.required,
      this.trimText({ error: 'trimText' }),
    ]);
  }

  get validationDescriptionAmbassador() {
    return Validators.compose([
      Validators.required,
      Validators.minLength(5),
      Validators.maxLength(600),
      this.trimText({ error: 'trimText' }),
    ]);
  }

  get validationAmbassadorNameSurname() {
    return Validators.compose([
      Validators.required,
      Validators.minLength(5),
      Validators.maxLength(600),
      this.trimText({ error: 'trimText' }),
    ]);
  }

  get validationAmbassadorTextInputs() {
    return Validators.compose([
      Validators.required,
      Validators.minLength(3),
      Validators.maxLength(600),
      this.trimText({ error: 'trimText' }),
    ]);
  }

  get validationBankAccountCountryIso() {
    return [
      Validators.maxLength(2),
      Validators.minLength(2),
      Validators.required,
      this.regexValidator(ValidationRegEx.onlyLetters),
      this.trimText({ error: 'trimText' }),
    ];
  }

  get validationBankAccountBranchCode() {
    return [
      Validators.maxLength(5),
      Validators.minLength(5),
      Validators.required,
      this.regexValidator(ValidationRegEx.integer),
      this.trimText({ error: 'trimText' }),
    ];
  }

  get validationBankAccountBankName() {
    return [
      Validators.maxLength(50),
      Validators.required,
      this.regexValidator(ValidationRegEx.onlyLettersAndNumbers),
      this.trimText({ error: 'trimText' }),
    ];
  }

  get validationBankAccountInstitutionNumber() {
    return [
      Validators.minLength(3),
      Validators.maxLength(4),
      Validators.required,
      this.regexValidator(ValidationRegEx.integer),
      this.trimText({ error: 'trimText' }),
    ];
  }

  get validationBankAccountSortCode() {
    return [
      Validators.minLength(6),
      Validators.maxLength(6),
      Validators.required,
      this.regexValidator(ValidationRegEx.integer),
      this.trimText({ error: 'trimText' }),
    ];
  }

  get validationBankAccountAba() {
    return [
      Validators.minLength(9),
      Validators.maxLength(9),
      Validators.required,
      this.regexValidator(ValidationRegEx.integer),
      this.trimText({ error: 'trimText' }),
    ];
  }

  get validationBankAccountNumber() {
    return [Validators.minLength(1), Validators.required, this.trimText({ error: 'trimText' })];
  }

  get validationBankAccountNumberGB() {
    return [
      Validators.minLength(8),
      Validators.maxLength(8),
      Validators.required,
      this.regexValidator(ValidationRegEx.integer),
      this.trimText({ error: 'trimText' }),
    ];
  }

  get validationBankAccountNumberCA() {
    return [
      Validators.minLength(1),
      Validators.maxLength(20),
      Validators.required,
      this.regexValidator(ValidationRegEx.integer),
      this.trimText({ error: 'trimText' }),
    ];
  }

  get validationBankAccountNumberUS() {
    return [
      Validators.minLength(1),
      Validators.required,
      this.regexValidator(ValidationRegEx.integer),
      this.trimText({ error: 'trimText' }),
    ];
  }

  get validationBankAccountBic() {
    return [
      Validators.minLength(4),
      Validators.maxLength(20),
      Validators.required,
      this.trimText({ error: 'trimText' }),
    ];
  }

  get validationBaseText() {
    return [
      Validators.minLength(2),
      Validators.maxLength(255),
      this.regexValidator(ValidationRegEx.text),
    ];
  }

  trimText({ error }): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      return control.value.trim().length > 0 ? null : error;
    };
  }

  get validationBaseTextRequired() {
    return [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(255),
      this.regexValidator(ValidationRegEx.text),
    ];
  }

  get validationRequired() {
    return [Validators.required];
  }

  get validationRequiredTrim() {
    return [Validators.required, this.trimText({ error: 'trimText' })];
  }

  get validationUrl() {
    return [Validators.required, this.regexValidator(ValidationRegEx.url)];
  }

  get validationUrlNotRequired() {
    return [this.regexValidator(ValidationRegEx.url)];
  }

  get validationSocialMediaLink() {
    return [this.regexValidator(ValidationRegEx.socialMediaLink)];
  }

  get validationBase() {
    return [Validators.minLength(2), Validators.maxLength(255)];
  }

  get validationDonate() {
    return [
      Validators.min(1),
      this.regexValidator(ValidationRegEx.integer),
      Validators.maxLength(4),
    ];
  }

  get validationParticipantsProject() {
    return [Validators.min(0), this.regexValidator(ValidationRegEx.integer)];
  }

  get validationDonateMinimum3() {
    return [
      Validators.min(3),
      this.regexValidator(ValidationRegEx.integer),
      Validators.maxLength(4),
    ];
  }

  get validationComment() {
    return [
      Validators.minLength(2),
      Validators.maxLength(500),
      (control) => {
        if (control.value.trim().length === 0) {
          return {
            noSpace: true,
          };
        }
        return null;
      },
    ];
  }

  get validationIban() {
    return [
      Validators.required,
      (control) => {
        // https://github.com/arhs/iban.js
        if (!IBAN.isValid(control.value)) {
          return {
            iban: true,
          };
        }
        return null;
      },
    ];
  }

  get validationIbanPL() {
    return [
      Validators.required,
      (control) => {
        // https://github.com/arhs/iban.js
        if (!IBAN.isValid(control.value) || control.value.toUpperCase().indexOf('PL') < 0) {
          return {
            iban: true,
          };
        }
        return null;
      },
    ];
  }

  get validationLongText() {
    return [Validators.maxLength(500)];
  }

  get validationTextareaLongText() {
    return [Validators.maxLength(1000)];
  }

  get validationEmail() {
    return [Validators.required, Validators.email, this.regexValidator(ValidationRegEx.email)];
  }

  get validationEmailNotRequired() {
    return [Validators.email, this.regexValidator(ValidationRegEx.email)];
  }

  get validationContactTopic() {
    return [Validators.required, Validators.minLength(2), Validators.maxLength(50)];
  }

  get validationContactEmail() {
    return [
      Validators.required,
      Validators.email,
      this.regexValidator(ValidationRegEx.email),
      Validators.minLength(2),
      Validators.maxLength(50),
    ];
  }

  get validationContactMessage() {
    return [Validators.required, Validators.minLength(2), Validators.maxLength(510)];
  }

  get validationCheckbox() {
    return [Validators.requiredTrue];
  }

  regexValidator({ regex, error }): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      const valid = regex.test(control.value);
      return valid ? null : error;
    };
  }
}
